import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaGraduationCap, FaLanguage, FaBrain, FaPencilAlt, FaGamepad, FaFlask } from "react-icons/fa";
import drawingRoom from "../assets/drawingroom.png";
import musicRoom from "../assets/unnamed-12.png";
import computerLab from "../assets/thumbnail_dil_1_OLHP_786x400.jpg";
import library from "../assets/DMIT.webp";
import smartClass from "../assets/smartclass.png";
import danceRoom from "../assets/Midbrain.jpg";

const facilities = [
  {
    name: "Fun and Learn",
    image: drawingRoom,
    description: "Interactive learning through play-based activities that make education enjoyable and memorable.",
    icon: FaGamepad,
    color: "from-orange-400 to-red-400",
    bgColor: "bg-gradient-to-br from-orange-50 to-red-50"
  },
  {
    name: "Phonics",
    image: musicRoom,
    description: "Building strong foundation in reading and pronunciation through systematic phonics instruction.",
    icon: FaLanguage,
    color: "from-blue-400 to-indigo-400",
    bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50"
  },
  {
    name: "Language Classes",
    subtitle: "English, Marathi, Hindi",
    image: computerLab,
    description: "Comprehensive multilingual education to develop communication skills in multiple languages.",
    icon: FaGraduationCap,
    color: "from-green-400 to-teal-400",
    bgColor: "bg-gradient-to-br from-green-50 to-teal-50"
  },
  {
    name: "DMIT Test",
    image: library,
    description: "Dermatoglyphics Multiple Intelligence Test to understand each child's unique learning potential.",
    icon: FaBrain,
    color: "from-purple-400 to-pink-400",
    bgColor: "bg-gradient-to-br from-purple-50 to-pink-50"
  },
  {
    name: "Handwriting",
    image: smartClass,
    description: "Developing fine motor skills and beautiful handwriting through structured practice sessions.",
    icon: FaPencilAlt,
    color: "from-yellow-400 to-orange-400",
    bgColor: "bg-gradient-to-br from-yellow-50 to-orange-50"
  },
  // {
  //   name: "Mid Brain Activity",
  //   image: danceRoom,
  //   description: "Enhancing cognitive abilities and brain development through specialized mid-brain activation techniques.",
  //   icon: FaFlask,
  //   color: "from-cyan-400 to-blue-400",
  //   bgColor: "bg-gradient-to-br from-cyan-50 to-blue-50"
  // },
];

const FacilitiesComponent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('facilities-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="facilities-section" className="py-20 bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-pink-200/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-200/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-6 py-2 rounded-full text-sm font-semibold mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaGraduationCap />
            <span>Our Facilities</span>
          </motion.div>

          <h2 className="text-4xl lg:text-6xl font-bold font-platypi text-gray-800 mb-6">
            <span className="gradient-text bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              World-Class
            </span>
            <br />
            <span className="text-gray-700">Learning Environment</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our state-of-the-art facilities designed to nurture every aspect
            of your child's development and learning journey.
          </p>
        </motion.div>

        {/* Facilities Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {facilities.map((facility, index) => {
            const IconComponent = facility.icon;
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                className="group"
              >
                <div className={`modern-card ${facility.bgColor} h-full relative overflow-hidden`}>
                  {/* Card Background Pattern */}
                  <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                    <div className={`w-full h-full bg-gradient-${facility.color} rounded-full transform rotate-45`}></div>
                  </div>

                  {/* Facility Image */}
                  <div className="relative overflow-hidden rounded-t-2xl">
                    <motion.img
                      src={facility.image}
                      alt={facility.name}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />

                    {/* Overlay with Icon */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4">
                        <div className={`w-12 h-12 bg-gradient-${facility.color} rounded-full flex items-center justify-center text-white shadow-lg`}>
                          <IconComponent size={20} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    {/* Title */}
                    <div>
                      <h3 className="text-xl font-bold font-platypi text-gray-800 mb-1">
                        {facility.name}
                      </h3>
                      {facility.subtitle && (
                        <p className="text-sm text-gray-600 font-medium">
                          {facility.subtitle}
                        </p>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {facility.description}
                    </p>

                    {/* Learn More Button */}
                    <motion.button
                      className={`w-full bg-gradient-${facility.color} text-white py-2 px-4 rounded-xl font-semibold text-sm shadow-medium hover:shadow-strong transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Learn More
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        {/* <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <p className="text-lg text-gray-600 mb-6">
            Want to see our facilities in person?
          </p>
          <motion.a
            href="https://wa.me/919834387679?text=Hi! I want to see your world-class facilities in person at Smart Educare Center. Please schedule a tour for me so I can explore the learning environment, meet the teachers, and see all the amazing facilities you offer. Let me know available dates and times that work best. Thank you!"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-medium hover:shadow-strong transition-all duration-300 inline-flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
            </svg>
            <span>Schedule a Tour</span>
            <motion.div
              className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </motion.div>
          </motion.a>
        </motion.div> */}
      </div>
    </section>
  );
};

export default FacilitiesComponent;
